import {addDoc, collection, doc, getDocs, query, updateDoc, where} from "firebase/firestore";
import {Button, Col, Form, Modal, Row, Spinner, Table} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faSave} from "@fortawesome/free-solid-svg-icons";
import {useEffect, useMemo, useState} from "react";
import {
    dateToString,
    removeEmptyFields,
    showErrorsListInToast,
    textToFloat, textToInt,
    zeroToEmptyStringAsCurrency,
} from "../../tools";
import {EMPTY_EMPLOYEE_PPH, RREmployeePPH, RREmployeePPHs, RREmployeeType} from "../../types/RREmployeeType";
import {appAuth, appFirestore} from "../../../index";
import ReactDatePicker from "react-datepicker";
import {MaybeDate, MaybeNumber} from "../../types/Maybe";
import {RRDEPARTMENT_MONTAGE} from "../../types/RRDepartmentType";
import {useAppSelector} from "../../hooks";
import {selectUsers} from "../../../features/AppAuthSlice";
import {useUpdateEmail, useUpdateProfile} from "react-firebase-hooks/auth";
import {selectCurrentEmployee, selectDepartment} from "../../../features/AppStateSlice";
import RightAlignedColumn from "../../components/RightAlignedColumn";
import CenteredColumn from "../../components/CenteredColumn";
import {AddIconButton, ConfirmIconButton, DeleteIconButton} from "../../components/IconButtons";
import StringInputField from "../../components/inputs/StringInputField";
import {RRTruckType} from "../../types/RRTruckType";
import NumberInputField from "../../components/inputs/NumberInputField";


export interface TruckDetailsProps {
    truck: RRTruckType;
    onDone: () => any;
}

export function TruckDetails({
                                 truck,
                                 onDone
                             }: TruckDetailsProps) {
    const [saving, setSaving] = useState(false);
    const [editTruck, setEditTruck] = useState<RRTruckType>({...truck});

    useEffect(() => {
        setEditTruck(truck);
    }, [truck]);

    const save = async () => {
        setSaving(true);
        const trucksRef = collection(appFirestore, "trucks");

        try {
            if (editTruck.id) {
                // Update existing truck
                const truckRef = doc(appFirestore, "trucks", editTruck.id);
                await updateDoc(truckRef, removeEmptyFields({
                    name: editTruck.name,
                    plate: editTruck.plate,
                    isOwn: editTruck.isOwn,
                    consumption: editTruck.consumption,
                    lastKm: editTruck.lastKm,
                    isActive: editTruck.isActive
                }));
            } else {
                // Add new truck
                await addDoc(trucksRef, removeEmptyFields({
                    name: editTruck.name,
                    plate: editTruck.plate,
                    isOwn: editTruck.isOwn,
                    consumption: editTruck.consumption,
                    lastKm: editTruck.lastKm,
                    isActive: editTruck.isActive
                }));
            }
            onDone();
        } catch (e: any) {
            showErrorsListInToast("Грешка", [e.message]);
        } finally {
            setSaving(false);
        }
    }


    const isValid = useMemo(() => {
        return (editTruck &&
            editTruck.name &&
            editTruck.plate &&
            editTruck.name.trim().length > 0 &&
            editTruck.plate.trim().length > 0 &&
            editTruck.consumption > 0 &&
            editTruck.lastKm > 0
        );
    }, [editTruck]);

    return (
        <Form>
            <Modal.Body>
                <h5 className={"mb-3"}>
                    Детайли за превозно средство
                </h5>

                <Row>
                    <Col>
                        <StringInputField
                            label={"Име на превозното средство"}
                            value={editTruck?.name}
                            required={true}
                            onChange={(value) => {
                                setEditTruck({
                                    ...editTruck!,
                                    name: value
                                });
                            }}
                        />
                    </Col>
                    <Col>
                        <StringInputField
                            label={"Регистрационен номер"}
                            value={editTruck?.plate || ""}
                            required={true}
                            onChange={(value) => {
                                setEditTruck({
                                    ...editTruck!,
                                    plate: value
                                });
                            }}
                        />
                    </Col>
                    <Col className={"pt-4"}>
                        <Row>
                            <Col xs={12}>
                                <Form.Group controlId={"isOwn"}>
                                    <Form.Check
                                        type={"checkbox"}
                                        label={"Собствено превозно средство"}
                                        checked={editTruck?.isOwn || false}
                                        onChange={(e) => {
                                            setEditTruck({
                                                ...editTruck!,
                                                isOwn: e.target.checked
                                            });
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Form.Group controlId={"isActive"}>
                                    <Form.Check
                                        type={"checkbox"}
                                        label={"Активно превозно средство"}
                                        checked={editTruck?.isActive || false}
                                        onChange={(e) => {
                                            setEditTruck({
                                                ...editTruck!,
                                                isActive: e.target.checked
                                            });
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row className={"gap-2"}>
                    <Col>
                        <NumberInputField
                            label={"Среден разход на гориво (л/100км)"}
                            value={editTruck?.consumption || 0}
                            required={true}
                            onChange={(value) => {
                                setEditTruck({
                                    ...editTruck!,
                                    consumption: value
                                });
                            }}
                            formatter={value => (value ? textToFloat(value.toString())?.toString()! : "0")}
                        />
                    </Col>
                    <Col>
                        <NumberInputField
                            label={"Последен километраж"}
                            value={editTruck?.lastKm || 0}
                            required={true}
                            onChange={(value) => {
                                setEditTruck({
                                    ...editTruck!,
                                    lastKm: value
                                });
                            }}
                            formatter={value => textToInt(value?.toString() ?? "0")?.toString() ?? "0"}
                        />
                    </Col>
                    <Col />
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Row className={"w-100"}>
                    {
                        saving && <Col xs={12} className={"text-center"}>
                            <Spinner animation={"border"}/>
                        </Col>
                    }

                    {
                        !saving &&
                        <>
                            <Col xs={6}>
                                <Button variant={"outline-secondary"} onClick={() => {
                                    onDone()
                                }}>
                                    <FontAwesomeIcon icon={faArrowLeft}/> Към списъка
                                </Button>
                            </Col>
                            <Col xs={6} className={"text-end"}>
                                <Button
                                    disabled={isValid !== true}
                                    onClick={save}
                                >
                                    <FontAwesomeIcon icon={faSave}/> Запиши
                                </Button>
                            </Col>
                        </>
                    }
                </Row>
            </Modal.Footer>
        </Form>
    )
}
